/**
 * Lead Command Center (Phase 9D)
 * Comprehensive lead view with AI Sales Intelligence, Audit, Proposal, Timeline, Notes, Tasks
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { downloadFile } from '@/lib/downloadFile';
import LeadEnrichmentPanel from './LeadEnrichmentPanel';
import OutreachApprovalQueue, { createDraftFromToolkit } from '@/revenue/OutreachApprovalQueue';
import {
  ArrowLeft,
  Save,
  Target,
  Sparkles,
  Mail,
  Phone,
  Globe,
  MapPin,
  Star,
  Edit,
  FileText,
  ClipboardList,
  TrendingUp,
  Zap,
  Clock,
  User,
  DollarSign,
  Brain,
  MessageSquare,
  CheckCircle2,
  AlertCircle,
  BarChart3,
  Trash2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mirrors the backend's require_module("clients", user) gate on
// POST /leads/{id}/convert - only roles that can manage client records
// can create one, even though any sales role can see/edit the lead itself.
const CAN_CONVERT_ROLES = ['Master Administrator', 'Operations Manager', 'Client Success Manager'];

// The only fields the Business Profile tab lets you edit. Save sends just
// these - PUT /phase9/leads/{id} $sets whatever it receives, so sending a
// whole lead snapshot would roll back anything changed since it was taken
// (status after Convert, audit_id/proposal_id after generating, ...).
const EDITABLE_FIELDS = ['email', 'phone', 'website', 'city', 'state', 'category'];
const pickEditable = (source) => Object.fromEntries(EDITABLE_FIELDS.map((field) => [field, source?.[field] ?? '']));

// Toolkit fields that can become a sendable outreach draft (email / SMS) -
// mirrors TOOLKIT_SOURCES in backend/outreach_routes.py. Drafts still need
// a Sales Manager / Master Admin approval before anything is sent.
const DRAFTABLE_TOOLKIT_KEYS = ['first_email', 'follow_up_email_1', 'follow_up_email_2', 'sms_draft'];

const LeadCommandCenter = ({ user, onNavigate, selectedLead: initialLead }) => {
  const [lead, setLead] = useState(initialLead || null);
  const [loading, setLoading] = useState(!initialLead);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [activeTab, setActiveTab] = useState('intelligence');
  
  // Phase 9D state
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [intelligence, setIntelligence] = useState(null);
  const [audit, setAudit] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [toolkit, setToolkit] = useState(null);
  const [note, setNote] = useState('');
  const [generating, setGenerating] = useState(false);
  const [converting, setConverting] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [creatingDraftKey, setCreatingDraftKey] = useState(null);
  const [draftsRefreshKey, setDraftsRefreshKey] = useState(0);

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${user.token}`,
    'Content-Type': 'application/json'
  }), [user.token]);

  const downloadPdf = async (type, id) => {
    try {
      await downloadFile(`${API}/${type}/${id}/pdf`, user.token, `${type === 'audits' ? 'Audit' : 'Proposal'}_${lead?.clinic_name || id.slice(0, 8)}.pdf`.replace(/\s+/g, '_'));
    } catch (err) {
      toast.error(err.message);
    }
  };

  useEffect(() => {
    if (initialLead) {
      setLead(initialLead);
      setIntelligence(initialLead.ai_sales_intelligence);
      // A toolkit generated earlier is stored on the lead - show it rather
      // than the "Generate Toolkit" CTA every time the lead is reopened.
      setToolkit(initialLead.ai_sales_toolkit || null);
      loadActivities(initialLead.id);
      loadAuditAndProposal(initialLead);
    }
  }, [initialLead]);

  const handleCreateOutreachDraft = async (key) => {
    setCreatingDraftKey(key);
    try {
      await createDraftFromToolkit(user, lead.id, key);
      toast.success('Draft created - edit it and submit it for approval below');
      setDraftsRefreshKey((n) => n + 1);
      loadActivities(lead.id);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreatingDraftKey(null);
    }
  };

  const loadActivities = async (leadId) => {
    setActivitiesLoading(true);
    try {
      const response = await fetch(`${API}/phase9/leads/${leadId}/activities?limit=50`, {
        headers: authHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setActivities(data.activities || []);
      }
    } catch (error) {
      console.error('Error loading activities:', error);
    } finally {
      setActivitiesLoading(false);
    }
  };

  const loadAuditAndProposal = async (leadObj) => {
    // Takes the lead just received explicitly rather than reading `lead`
    // from component state - setLead() above is async, so `lead` in this
    // closure is still the previous render's value (null on first mount,
    // or the prior lead when navigating between leads) when this runs
    // synchronously right after setLead() in the same effect. Reading
    // state here meant audit_id/proposal_id were essentially never the
    // newly-opened lead's real values, so an existing audit/proposal
    // never loaded - the tabs always showed the empty "Generate" CTA
    // even when one had already been generated.
    try {
      if (leadObj?.audit_id) {
        const auditRes = await fetch(`${API}/audits/${leadObj.audit_id}`, { headers: authHeaders() });
        if (auditRes.ok) {
          const auditData = await auditRes.json();
          setAudit(auditData);
        }
      }
      if (leadObj?.proposal_id) {
        const proposalRes = await fetch(`${API}/proposals/${leadObj.proposal_id}`, { headers: authHeaders() });
        if (proposalRes.ok) {
          const proposalData = await proposalRes.json();
          setProposal(proposalData);
        }
      }
    } catch (error) {
      console.error('Error loading audit/proposal:', error);
    }
  };

  const handleSave = async () => {
    const original = pickEditable(lead);
    const changes = Object.fromEntries(EDITABLE_FIELDS.filter((field) => formData[field] !== original[field]).map((field) => [field, formData[field]]));
    if (!Object.keys(changes).length) {
      setEditMode(false);
      return;
    }
    setSaving(true);
    try {
      const response = await fetch(`${API}/phase9/leads/${lead.id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ data: changes })
      });
      if (response.ok) {
        // The route returns the updated lead itself, not {lead: ...}.
        const updated = await response.json();
        setLead(updated);
        setEditMode(false);
        toast.success('Lead updated successfully');
        loadActivities(lead.id);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(typeof err.detail === 'string' ? err.detail : 'Failed to update lead');
      }
    } catch (error) {
      toast.error('Error updating lead');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/phase9/leads/${lead.id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (response.ok) {
        toast.success('Lead archived');
        onNavigate('discovery');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to archive lead');
      }
    } catch (error) {
      toast.error('Error archiving lead');
    } finally {
      setSaving(false);
    }
  };

  const handleConvert = async () => {
    setConverting(true);
    try {
      const response = await fetch(`${API}/leads/${lead.id}/convert`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ data: {} })
      });
      if (response.ok) {
        const client = await response.json();
        setLead({ ...lead, status: 'Won', client_id: client.id });
        toast.success(`${client.clinic_name} converted to a client`);
        loadActivities(lead.id);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to convert lead');
      }
    } catch (error) {
      toast.error('Error converting lead');
    } finally {
      setConverting(false);
    }
  };

  const handleScore = async () => {
    setGenerating(true);
    try {
      const response = await fetch(`${API}/phase9/leads/${lead.id}/score`, {
        method: 'POST',
        headers: authHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setLead({ ...lead, ai_lead_score: data.score, ai_lead_score_label: data.label, ai_scoring_factors: data.factors });
        toast.success(`Lead scored: ${data.score} (${data.label})`);
        loadActivities(lead.id);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to score lead');
      }
    } catch (error) {
      toast.error('Error scoring lead');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateIntelligence = async () => {
    setGenerating(true);
    try {
      const response = await fetch(`${API}/phase9/leads/${lead.id}/sales-intelligence/generate`, {
        method: 'POST',
        headers: authHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setIntelligence(data);
        setLead({ ...lead, ai_sales_intelligence: data });
        toast.success('AI Sales Intelligence generated successfully');
        loadActivities(lead.id);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate intelligence');
      }
    } catch (error) {
      toast.error('Error generating intelligence');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateAudit = async () => {
    setGenerating(true);
    try {
      const response = await fetch(`${API}/phase9/leads/${lead.id}/audit/generate`, {
        method: 'POST',
        headers: authHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAudit(data.audit);
        setLead({ ...lead, audit_id: data.audit.id, audit_status: 'Generated' });
        toast.success(data.duplicate_prevented ? 'Existing audit found' : 'Audit generated successfully');
        loadActivities(lead.id);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate audit');
      }
    } catch (error) {
      toast.error('Error generating audit');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateProposal = async () => {
    setGenerating(true);
    try {
      const response = await fetch(`${API}/phase9/leads/${lead.id}/proposal/generate`, {
        method: 'POST',
        headers: authHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setProposal(data.proposal);
        setLead({ ...lead, proposal_id: data.proposal.id, proposal_status: 'Draft', pipeline_stage: 'Proposal Sent' });
        toast.success(data.duplicate_prevented ? 'Existing proposal found' : 'Proposal generated successfully');
        loadActivities(lead.id);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate proposal');
      }
    } catch (error) {
      toast.error('Error generating proposal');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateToolkit = async () => {
    setGenerating(true);
    try {
      const response = await fetch(`${API}/phase9/leads/${lead.id}/toolkit/generate`, {
        method: 'POST',
        headers: authHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setToolkit(data.toolkit);
        toast.success('Sales toolkit generated (DRAFT MODE)');
        loadActivities(lead.id);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate toolkit');
      }
    } catch (error) {
      toast.error('Error generating toolkit');
    } finally {
      setGenerating(false);
    }
  };

  const handleAddNote = async () => {
    if (!note.trim()) return;
    try {
      const response = await fetch(`${API}/phase9/leads/${lead.id}/activities`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          data: {
            activity_type: 'note_added',
            title: 'Manual Note',
            description: note
          }
        })
      });
      if (response.ok) {
        toast.success('Note added');
        setNote('');
        loadActivities(lead.id);
      }
    } catch (error) {
      toast.error('Error adding note');
    }
  };

  const getScoreBadgeClass = (label) => {
    switch (label) {
      case 'Hot': return 'bg-red-500/20 text-red-300 border-red-500/30';
      case 'Warm': return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
      case 'Cold': return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'Bad Fit': return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default: return 'bg-white/10 text-white/60 border-white/20';
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'ai_intelligence_generated': return <Brain className="h-4 w-4" />;
      case 'audit_generated': return <FileText className="h-4 w-4" />;
      case 'proposal_generated': return <ClipboardList className="h-4 w-4" />;
      case 'outreach_draft_created': return <Mail className="h-4 w-4" />;
      case 'note_added': return <MessageSquare className="h-4 w-4" />;
      case 'pipeline_changed': return <TrendingUp className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  if (!lead) {
    return (
      <div className="page-stack">
        <div className="glass-panel text-center py-16">
          <p className="text-white/60">No lead selected</p>
          <Button className="ghost-btn mt-4" onClick={() => onNavigate('discovery')}>Go to Discovery</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-stack" data-testid="lead-command-center">
      {/* Header */}
      <section className="hero-panel ceo-hero glass-panel noise-overlay">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Button className="ghost-btn" onClick={() => onNavigate('discovery')} data-testid="back-to-discovery-btn">
              <ArrowLeft size={16} />
            </Button>
            <div>
              <p className="eyebrow">Lead Command Center — Phase 9D</p>
              <h2>{lead.clinic_name}</h2>
            </div>
          </div>
          <div className="hero-stats">
            <span>{lead.city}, {lead.state}</span>
            {lead.category && <span>{lead.category}</span>}
            {lead.ai_lead_score !== null && lead.ai_lead_score !== undefined && (
              <span>Score: {lead.ai_lead_score}</span>
            )}
            {intelligence?.ai_close_probability && (
              <span>Close Probability: {intelligence.ai_close_probability}%</span>
            )}
          </div>
        </div>
        <div className="flex gap-3 flex-wrap">
          {editMode ? (
            <>
              <Button className="ghost-btn" onClick={() => setEditMode(false)}>Cancel</Button>
              <Button className="primary-btn" onClick={handleSave} disabled={saving} data-testid="save-lead-btn">
                <Save size={16} />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          ) : (
            <>
              <Button className="ghost-btn" onClick={() => { setFormData(pickEditable(lead)); setEditMode(true); }} data-testid="edit-lead-btn">
                <Edit size={16} />
                Edit
              </Button>
              <Button className="ghost-btn" onClick={handleScore} disabled={generating} data-testid="rescore-lead-btn">
                <TrendingUp size={16} />
                {lead.ai_lead_score !== null && lead.ai_lead_score !== undefined ? 'Rescore' : 'Score'} Lead
              </Button>
              <Button className="ghost-btn" onClick={handleGenerateIntelligence} disabled={generating} data-testid="generate-intelligence-btn">
                <Brain size={16} />
                {intelligence ? 'Refresh' : 'Generate'} Intelligence
              </Button>
              <Button className="ghost-btn" onClick={handleGenerateAudit} disabled={generating} data-testid="generate-audit-btn">
                <FileText size={16} />
                {audit ? 'View' : 'Generate'} Audit
              </Button>
              <Button className="primary-btn" onClick={handleGenerateProposal} disabled={generating} data-testid="generate-proposal-btn">
                <ClipboardList size={16} />
                {proposal ? 'View' : 'Generate'} Proposal
              </Button>
              {lead.client_id ? (
                <Badge className="bg-green-500/20 text-green-300 border-green-500/30 flex items-center gap-1" data-testid="lead-converted-badge">
                  <CheckCircle2 size={14} />
                  Converted to Client
                </Badge>
              ) : (
                CAN_CONVERT_ROLES.includes(user?.role) && (
                  <Button className="primary-btn" onClick={handleConvert} disabled={converting} data-testid="convert-lead-btn">
                    <CheckCircle2 size={16} />
                    {converting ? 'Converting...' : 'Convert to Client'}
                  </Button>
                )
              )}
              <Button className="ghost-btn" onClick={async () => {
                // Runs enrichment inline (website + Google data when
                // configured) and returns the updated lead.
                setEnriching(true);
                try {
                  const response = await fetch(`${API}/phase9/leads/${lead.id}/enrich`, {
                    method: 'POST',
                    headers: authHeaders()
                  });
                  const data = await response.json().catch(() => ({}));
                  if (response.ok) {
                    if (data.lead) setLead(data.lead);
                    const notify = ['failed', 'skipped'].includes(data.outcome) ? toast.warning : toast.success;
                    notify(data.message || 'Lead enrichment started');
                  } else {
                    toast.error(data.detail || 'Lead enrichment failed');
                  }
                } catch (error) {
                  toast.error('Error enriching lead');
                } finally {
                  setEnriching(false);
                }
              }} disabled={generating || enriching || lead.enrichment_status === 'processing'} data-testid="enrich-lead-btn">
                <Zap size={16} />
                {enriching || lead.enrichment_status === 'processing' ? 'Enriching...' : ['enriched', 'completed'].includes(lead.enrichment_status) ? 'Re-enrich Lead' : 'Enrich Lead'}
              </Button>
              {user?.role === 'Master Administrator' && (
                <Button className="danger-btn" onClick={handleArchive} disabled={saving} data-testid="archive-lead-btn">
                  <Trash2 size={16} />
                  Archive Lead
                </Button>
              )}
            </>
          )}
        </div>
      </section>

      {/* Enrichment status + results */}
      <LeadEnrichmentPanel lead={lead} user={user} onLeadUpdated={setLead} />

      {/* Score Badges */}
      {(lead.ai_lead_score !== null || intelligence?.ai_priority_score) && (
        <section className="flex gap-4 flex-wrap">
          {lead.ai_lead_score !== null && lead.ai_lead_score !== undefined && (
            <div className="glass-panel flex-1">
              <p className="text-xs text-white/60 mb-2">AI Lead Score</p>
              <Badge className={`${getScoreBadgeClass(lead.ai_lead_score_label)} text-lg px-4 py-1.5`}>
                {lead.ai_lead_score} — {lead.ai_lead_score_label}
              </Badge>
            </div>
          )}
          {intelligence?.ai_priority_score && (
            <div className="glass-panel flex-1">
              <p className="text-xs text-white/60 mb-2">AI Priority Score</p>
              <Badge className="bg-[var(--gold)]/20 text-[var(--gold)] text-lg px-4 py-1.5">
                {intelligence.ai_priority_score}
              </Badge>
            </div>
          )}
          {intelligence?.ai_close_probability && (
            <div className="glass-panel flex-1">
              <p className="text-xs text-white/60 mb-2">Close Probability</p>
              <Badge className="bg-green-500/20 text-green-300 border-green-500/30 text-lg px-4 py-1.5">
                {intelligence.ai_close_probability}%
              </Badge>
            </div>
          )}
        </section>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="glass-panel flex-wrap">
          <TabsTrigger value="intelligence">AI Intelligence</TabsTrigger>
          <TabsTrigger value="business">Business Profile</TabsTrigger>
          <TabsTrigger value="audit">Audit {audit && '✓'}</TabsTrigger>
          <TabsTrigger value="proposal">Proposal {proposal && '✓'}</TabsTrigger>
          <TabsTrigger value="toolkit">Sales Toolkit</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="notes">Notes & Tasks</TabsTrigger>
        </TabsList>

        {/* AI Intelligence Tab */}
        <TabsContent value="intelligence">
          {!intelligence ? (
            <section className="glass-panel text-center py-16">
              <Brain className="h-16 w-16 text-white/20 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white/90 mb-2">Generate AI Sales Intelligence</h3>
              <p className="text-white/60 mb-6">Get 13 AI-powered insights including executive summary, pain points, sales strategy, and more</p>
              <Button className="primary-btn" onClick={handleGenerateIntelligence} disabled={generating} data-testid="intelligence-empty-generate-btn">
                <Sparkles size={16} />
                {generating ? 'Generating...' : 'Generate Intelligence'}
              </Button>
            </section>
          ) : (
            <div className="space-y-4">
              {/* Executive Summary */}
              <Card className="glass-panel border-white/10">
                <CardHeader>
                  <CardTitle className="text-white/90">Executive Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-white/70 leading-relaxed whitespace-pre-wrap">{intelligence.executive_summary}</p>
                </CardContent>
              </Card>

              {/* Strengths & Weaknesses */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="glass-panel border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white/90 text-sm flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-400" />
                      Business Strengths
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="list-disc list-inside space-y-1">
                      {intelligence.business_strengths?.map((strength, idx) => (
                        <li key={idx} className="text-white/70 text-sm">{strength}</li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                <Card className="glass-panel border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white/90 text-sm flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-orange-400" />
                      Business Weaknesses
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="list-disc list-inside space-y-1">
                      {intelligence.business_weaknesses?.map((weakness, idx) => (
                        <li key={idx} className="text-white/70 text-sm">{weakness}</li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>

              {/* Revenue Opportunity */}
              <Card className="glass-panel border-white/10">
                <CardHeader>
                  <CardTitle className="text-white/90 flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-green-400" />
                    Revenue Opportunity Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-white/60 mb-1">Est. Monthly Opportunity</p>
                      <p className="text-2xl font-semibold text-green-400">
                        ${intelligence.revenue_opportunity_analysis?.estimated_monthly_opportunity?.toLocaleString() || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-white/60 mb-1">Est. Annual Opportunity</p>
                      <p className="text-2xl font-semibold text-green-400">
                        ${intelligence.revenue_opportunity_analysis?.estimated_annual_opportunity?.toLocaleString() || 'N/A'}
                      </p>
                    </div>
                  </div>
                  <p className="text-white/70 text-sm leading-relaxed">{intelligence.revenue_opportunity_analysis?.analysis}</p>
                </CardContent>
              </Card>

              {/* Pain Points & Recommended Offer */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="glass-panel border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white/90 text-sm">Pain Points</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="list-disc list-inside space-y-1">
                      {intelligence.pain_points?.map((point, idx) => (
                        <li key={idx} className="text-white/70 text-sm">{point}</li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                <Card className="glass-panel border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white/90 text-sm">Recommended Offer</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-white/70 text-sm leading-relaxed">{intelligence.recommended_offer}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Sales Strategy & Next Action */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="glass-panel border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white/90 text-sm flex items-center gap-2">
                      <Target className="h-4 w-4 text-[var(--gold)]" />
                      Sales Strategy
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-white/70 text-sm leading-relaxed">{intelligence.sales_strategy}</p>
                  </CardContent>
                </Card>

                <Card className="glass-panel border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white/90 text-sm flex items-center gap-2">
                      <Zap className="h-4 w-4 text-[var(--gold)]" />
                      Next Best Action
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-white/70 text-sm leading-relaxed font-medium">{intelligence.next_best_action}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Contact Method & Time + Objection Predictions */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="glass-panel border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white/90 text-sm">Best Contact Method</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Badge className="bg-[var(--gold)]/20 text-[var(--gold)]">{intelligence.best_contact_method}</Badge>
                  </CardContent>
                </Card>

                <Card className="glass-panel border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white/90 text-sm">Best Contact Time</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-white/70 text-sm">{intelligence.best_contact_time}</p>
                  </CardContent>
                </Card>

                <Card className="glass-panel border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white/90 text-sm">Objection Predictions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="list-disc list-inside space-y-1">
                      {intelligence.objection_predictions?.slice(0, 3).map((obj, idx) => (
                        <li key={idx} className="text-white/70 text-xs">{obj}</li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>

              {/* Refresh Button */}
              <div className="flex justify-center">
                <Button className="ghost-btn" onClick={handleGenerateIntelligence} disabled={generating}>
                  <Sparkles size={16} />
                  {generating ? 'Regenerating...' : 'Regenerate Intelligence'}
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Business Profile Tab */}
        <TabsContent value="business">
          <section className="glass-panel">
            <h3 className="text-lg font-semibold text-white/90 mb-4">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {EDITABLE_FIELDS.map((field) => (
                <div key={field}>
                  <label className="text-xs text-white/60 mb-2 block capitalize">{field}</label>
                  {editMode ? (
                    <Input
                      value={formData[field] || ''}
                      onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                      className="bg-white/5 border-white/10 text-white"
                    />
                  ) : (
                    <p className="text-white/80">{lead[field] || '—'}</p>
                  )}
                </div>
              ))}
            </div>
          </section>
        </TabsContent>

        {/* Audit Tab */}
        <TabsContent value="audit">
          {!audit ? (
            <section className="glass-panel text-center py-16">
              <FileText className="h-16 w-16 text-white/20 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white/90 mb-2">Generate AI Audit</h3>
              <p className="text-white/60 mb-6">Analyze website, Google presence, social media, and growth potential</p>
              <Button className="primary-btn" onClick={handleGenerateAudit} disabled={generating}>
                <Sparkles size={16} />
                {generating ? 'Generating...' : 'Generate Audit'}
              </Button>
            </section>
          ) : (
            <section className="glass-panel">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-white/90">AI Audit Summary</h3>
                  <p className="text-sm text-white/60 mt-1">Overall Growth Score: {audit.overall_medspa_growth_score}</p>
                </div>
                <Button className="ghost-btn" onClick={() => downloadPdf('audits', audit.id)}>
                  Download Audit PDF
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-white/60 mb-1">Website</p>
                  <p className="text-xl font-semibold text-white/90">{audit.website_conversion_score}</p>
                </div>
                <div>
                  <p className="text-xs text-white/60 mb-1">Google</p>
                  <p className="text-xl font-semibold text-white/90">{audit.google_reputation_score}</p>
                </div>
                <div>
                  <p className="text-xs text-white/60 mb-1">Social Media</p>
                  <p className="text-xl font-semibold text-white/90">{audit.social_media_score}</p>
                </div>
              </div>
            </section>
          )}
        </TabsContent>

        {/* Proposal Tab */}
        <TabsContent value="proposal">
          {!proposal ? (
            <section className="glass-panel text-center py-16">
              <ClipboardList className="h-16 w-16 text-white/20 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white/90 mb-2">Generate Proposal</h3>
              <p className="text-white/60 mb-6">Create custom proposal: $12,500 setup + $800/month management</p>
              <Button className="primary-btn" onClick={handleGenerateProposal} disabled={generating}>
                <Sparkles size={16} />
                {generating ? 'Generating...' : 'Generate Proposal'}
              </Button>
            </section>
          ) : (
            <section className="glass-panel">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-white/90">Proposal Summary</h3>
                  <p className="text-sm text-white/60 mt-1">Status: {proposal.status}</p>
                </div>
                <Button className="ghost-btn" onClick={() => downloadPdf('proposals', proposal.id)}>
                  Download Proposal PDF
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div>
                  <p className="text-xs text-white/60 mb-1">Setup Fee</p>
                  <p className="text-2xl font-semibold text-[var(--gold)]">${proposal.setup_fee?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-white/60 mb-1">Monthly Management</p>
                  <p className="text-2xl font-semibold text-[var(--gold)]">${proposal.monthly_management?.toLocaleString()}/mo</p>
                </div>
                <div>
                  <p className="text-xs text-white/60 mb-1">Ad Spend</p>
                  <p className="text-lg font-semibold text-white/90">{proposal.ad_spend}</p>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-white/90 mb-2">Executive Summary</h4>
                <p className="text-white/70 text-sm leading-relaxed">{proposal.executive_summary}</p>
              </div>
            </section>
          )}
        </TabsContent>

        {/* Sales Toolkit Tab */}
        <TabsContent value="toolkit">
          {!intelligence ? (
            <section className="glass-panel text-center py-16">
              <AlertCircle className="h-16 w-16 text-orange-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white/90 mb-2">Generate Intelligence First</h3>
              <p className="text-white/60 mb-6">AI Sales Intelligence is required before generating sales toolkit</p>
              <Button className="primary-btn" onClick={handleGenerateIntelligence} disabled={generating}>
                <Brain size={16} />
                Generate Intelligence
              </Button>
            </section>
          ) : !toolkit ? (
            <section className="glass-panel text-center py-16">
              <Mail className="h-16 w-16 text-white/20 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white/90 mb-2">Generate Sales Toolkit</h3>
              <p className="text-white/60 mb-6">Create email sequences, call scripts, and objection handling guide (DRAFT MODE)</p>
              <Button className="primary-btn" onClick={handleGenerateToolkit} disabled={generating}>
                <Sparkles size={16} />
                {generating ? 'Generating...' : 'Generate Toolkit'}
              </Button>
            </section>
          ) : (
            <div className="space-y-4">
              <div className="glass-panel bg-orange-500/10 border-orange-500/30">
                <p className="text-orange-300 text-sm flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  DRAFT MODE: Nothing is sent automatically. Turn an email or SMS into a draft below - it is only sent after a Sales Manager or the Master Administrator approves it.
                </p>
              </div>

              {['first_email', 'follow_up_email_1', 'follow_up_email_2', 'cold_call_opening', 'cold_call_script', 'discovery_questions', 'objection_handling_guide', 'closing_script', 'instagram_dm', 'facebook_message', 'sms_draft', 'voicemail_script', 'linkedin_message'].map((key) => (
                toolkit[key] && (
                  <Card key={key} className="glass-panel border-white/10">
                    <CardHeader>
                      <CardTitle className="text-white/90 text-sm capitalize flex items-center gap-2">
                        {key.includes('email') && <Mail className="h-4 w-4" />}
                        {key.includes('call') && <Phone className="h-4 w-4" />}
                        {key.includes('instagram') && <MessageSquare className="h-4 w-4" />}
                        {key.includes('facebook') && <MessageSquare className="h-4 w-4" />}
                        {key.includes('sms') && <MessageSquare className="h-4 w-4" />}
                        {key.includes('voicemail') && <Phone className="h-4 w-4" />}
                        {key.includes('linkedin') && <User className="h-4 w-4" />}
                        {key.replace(/_/g, ' ')}
                        {DRAFTABLE_TOOLKIT_KEYS.includes(key) && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="ml-auto border-white/20 text-white/80 normal-case"
                            onClick={() => handleCreateOutreachDraft(key)}
                            disabled={creatingDraftKey !== null}
                            data-testid={`toolkit-create-draft-${key}`}
                          >
                            {creatingDraftKey === key ? 'Creating…' : 'Create draft for approval'}
                          </Button>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <pre className="text-white/70 text-sm leading-relaxed whitespace-pre-wrap font-sans">{toolkit[key]}</pre>
                    </CardContent>
                  </Card>
                )
              ))}
            </div>
          )}
          {lead?.id && (
            <div className="mt-6">
              <OutreachApprovalQueue user={user} leadId={lead.id} title="Outreach drafts for this lead" refreshKey={draftsRefreshKey} />
            </div>
          )}
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline">
          <section className="glass-panel">
            <h3 className="text-lg font-semibold text-white/90 mb-4">Activity Timeline</h3>
            {activitiesLoading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full bg-white/10" />)}
              </div>
            ) : activities.length === 0 ? (
              <div className="text-center py-12">
                <Clock className="h-12 w-12 text-white/20 mx-auto mb-3" />
                <p className="text-white/60">No activities yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {activities.map((activity) => (
                  <div key={activity.id} className="flex items-start gap-3 p-4 border border-white/10 rounded-xl">
                    <div className="h-8 w-8 rounded-full bg-white/10 flex items-center justify-center text-white/60">
                      {getActivityIcon(activity.activity_type)}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-white/90">{activity.title}</h4>
                      <p className="text-xs text-white/60 mt-1">{activity.description}</p>
                      <p className="text-xs text-white/40 mt-2">
                        {new Date(activity.created_at).toLocaleString()} by {activity.created_by}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </TabsContent>

        {/* Notes & Tasks Tab */}
        <TabsContent value="notes">
          <section className="glass-panel">
            <h3 className="text-lg font-semibold text-white/90 mb-4">Add Note</h3>
            <div className="flex gap-3">
              <Textarea
                placeholder="Add a note or task..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-white/40"
                rows={3}
              />
              <Button className="primary-btn" onClick={handleAddNote} disabled={!note.trim()} data-testid="add-note-btn">
                <MessageSquare size={16} />
                Add Note
              </Button>
            </div>
          </section>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LeadCommandCenter;
